// Learn & Earn test history screens (LB-366, signed off 23 Sep — canvas
// https://claude.ai/artifact/N6oRvRthix3ZAj8DeL1xZd).
//
//   Kid Mode    KidTestHistory      "My test history": filter chips → list → one test
//   Parent Mode TestHistoryEntry    the row at the top of Coach's summary / Set it yourself
//               ParentTestHistory   "{name}'s test history": filter chips → list
//               ParentTestDetail    one test, every question in the order asked
//               RecentTestsCard     the per-game card on Coach's game detail and on Set it
//                                   yourself; rows open the test, "See all {game} tests"
//
// Data: learningContent[childId].testHistory, written by App.jsx at settlement through
// src/lib/testHistory.js. The kid and parent screens read the same records and word them
// for their own reader: the kid view puts the misses first and never shows a cross; the
// parent view keeps the order the questions were asked.
import { Icon } from "./Common.jsx";
import { t as ct } from "../lib/content.js";
import { HISTORY_PER_GAME, findTest, historyGame, historyGameIds, historyList, historyStamp, liveRecord, payState, sumLine } from "../lib/testHistory.js";

const { useState } = React;

const isWords = (game) => game === "spelling" || game === "vocab" || game === "homework";

// ---- shared bits ------------------------------------------------------------------------

function FilterChips({ ids, value, onChange, kid }) {
  const base = "text-xs font-bold rounded-full px-3.5 min-h-[36px] border";
  const on = kid ? "bg-indigo-600 border-indigo-600 text-white" : "bg-pill border-pill text-slate-900";
  const off = kid ? "bg-white border-indigo-100 text-indigo-800" : "bg-white border-slate-200 text-slate-500";
  return (
    <div className="flex gap-2 flex-wrap" role="group" aria-label={ct("history.filter_label", "Filter by game")} data-testid="history-chips">
      {[null, ...ids].map((g) => (
        <button key={g || "all"} type="button" aria-pressed={value === g} onClick={() => onChange(g)} className={`${base} ${value === g ? on : off}`} data-testid={`history-chip-${g || "all"}`}>
          {g ? historyGameName(g) : ct("history.all", "All")}
        </button>
      ))}
    </div>
  );
}

// Literal keys, so the CMS tooling sees them. It sits here, after FilterChips, because
// scripts/cms-screens.mjs files a key under the component whose source it follows, and the
// chips are where these names first show. A game the map does not know yet falls back to the
// registry's own name, then to its id.
export function historyGameName(id) {
  const names = {
    spelling: ct("history.game.spelling", "Spelling"),
    vocab: ct("history.game.vocab", "Vocabulary"),
    ttstreaks: ct("history.game.ttstreaks", "Times tables"),
    mathsworkout: ct("history.game.mathsworkout", "Maths"),
    homework: ct("history.game.homework", "Homework"),
  };
  return names[id] || historyGame(id).name;
}

function GameDisc({ game, kid }) {
  return (
    <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center flex-shrink-0 ${kid ? "bg-indigo-50 text-indigo-600" : "bg-slate-50 text-slate-700"}`} aria-hidden="true">
      <Icon id={historyGame(game).icon} size={17} style={{ color: "currentColor" }} />
    </span>
  );
}

const scoreText = (r) => (r.stopped
  ? ct("adaptive.hub.recent_stopped_score", "{answered} of {total}", { answered: r.answered, total: r.total })
  : ct("adaptive.hub.recent_score", "{score} of {total}", { score: r.score, total: r.total }));

// ---- Kid Mode ---------------------------------------------------------------------------

// LB-364: a homework go has no pass mark. "Below pass" would be wrong on a 3 of 3, so a go
// that paid no tokens reads as what it is: practice, with its Oinks.
const isPractice = (r) => r && r.game === "homework";

function kidRowReward(r) {
  const s = payState(r);
  if (isPractice(r) && s !== "paid" && s !== "sent" && s !== "declined") return r.oinks > 0 ? ct("history.kid.row_oinks", "+{n} Oinks", { n: r.oinks }) : ct("history.kid.row_practice", "Practice");
  if (s === "paid") return ct("history.kid.row_tokens", "+{n} tokens", { n: r.tokens });
  if (s === "sent") return ct("history.kid.row_waiting", "Waiting for a grown-up");
  if (s === "declined") return ct("history.kid.row_declined", "Not this time");
  return ct("history.kid.row_none", "No tokens");
}

function kidPayLine(r) {
  const s = payState(r);
  const oinks = r.oinks > 0 ? ` ${ct("history.kid.pay_oinks", "You got {n} Oinks too.", { n: r.oinks })}` : "";
  if (isPractice(r) && s !== "paid" && s !== "sent" && s !== "declined") return r.oinks > 0 ? ct("history.kid.pay_practice_oinks", "A practice go. You earned {n} Oinks.", { n: r.oinks }) : ct("history.kid.pay_practice", "A practice go.");
  if (s === "stopped") return ct("history.kid.pay_stopped", "You stopped after {n} of {total} questions, so this test didn't pay. Finish a test to earn tokens.", { n: r.answered, total: r.total });
  if (s === "paid") return ct("history.kid.pay_paid", "You earned {n} tokens.", { n: r.tokens }) + oinks;
  if (s === "sent") return ct("history.kid.pay_sent", "{n} tokens are waiting for your grown-up to say yes.", { n: r.tokens }) + oinks;
  // A refusal is never attributed to an adult on the kid side (Alan, 19 Sep).
  if (s === "declined") return ct("history.kid.pay_declined", "Not this time.") + oinks;
  if (s === "used") return ct("history.kid.pay_used", "You'd already earned from this game this week, so no tokens this time.") + oinks;
  return ct("history.kid.pay_below", "Not quite enough for tokens this time. Keep practising and it will come.") + oinks;
}

function KidMissRow({ game, row }) {
  let lines;
  if (row.skipped) lines = [ct("history.kid.skipped", "You skipped this one")];
  else if (row.timedOut) lines = [ct("history.kid.timed_out", "The timer ran out")];
  else if (game === "vocab") lines = [ct("history.kid.picked", "You picked {given}", { given: row.given })];
  else if (game === "spelling") lines = [ct("history.kid.wrote", "You wrote {given}", { given: row.given })];
  else lines = [ct("history.kid.answered_sum", "You answered {given}", { given: row.given })];
  if (game === "vocab") lines.push(ct("history.kid.means", "It means {answer}", { answer: row.answer }));
  else if (game !== "spelling") lines.push(ct("history.kid.the_answer", "The answer is {answer}", { answer: row.answer }));
  return (
    <div className="kid-row flex items-center gap-3 py-2.5" data-testid="kid-history-miss">
      <span className="w-8 h-8 rounded-full inline-flex items-center justify-center flex-shrink-0 bg-amber-50 text-amber-700" aria-hidden="true"><Icon id="ic-reassure" size={16} style={{ color: "currentColor" }} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-base font-extrabold text-slate-900 break-words">{row.q}</span>
        {lines.map((l, i) => <span key={i} className="block text-xs text-slate-600">{l}</span>)}
      </span>
    </div>
  );
}

function KidTestDetail({ rec }) {
  const misses = rec.questions.filter((q) => !q.correct);
  const got = rec.questions.filter((q) => q.correct);
  return (
    <div className="flex flex-col gap-3.5" data-testid="kid-history-detail">
      <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <GameDisc game={rec.game} kid />
          <span className="flex-1 min-w-0"><span className="block text-sm font-extrabold text-slate-900">{historyGameName(rec.game)}</span><span className="block text-xs text-slate-500">{historyStamp(rec.at)}</span></span>
          {rec.stopped && <span className="text-xs font-bold rounded px-1.5 py-0.5 bg-amber-50 text-amber-800">{ct("history.kid.tag_stopped", "Stopped early")}</span>}
        </div>
        <div className="text-2xl font-extrabold text-slate-900 mt-1.5" data-testid="kid-history-score">{rec.stopped
          ? ct("history.kid.answered", "{n} answered", { n: rec.answered })
          : ct("history.kid.score", "{score} out of {total}", { score: rec.score, total: rec.total })}</div>
        {rec.game === "ttstreaks" && rec.streak != null && <div className="text-sm text-slate-600">{ct("history.kid.streak", "Best streak: {n}", { n: rec.streak })}</div>}
        <div className="text-sm text-slate-600" data-testid="kid-history-pay">{kidPayLine(rec)}</div>
      </div>
      {misses.length > 0 && (
        <div>
          <div className="text-base font-extrabold text-slate-900 mb-1.5">{isWords(rec.game) ? ct("history.kid.practise_words", "Words to practise") : ct("history.kid.practise_sums", "Ones to practise")}</div>
          <div className="kid-rows">{misses.map((row, i) => <KidMissRow key={i} game={rec.game} row={row} />)}</div>
        </div>
      )}
      {got.length > 0 && (
        <div>
          <div className="text-base font-extrabold text-slate-900 mb-1.5">{ct("history.kid.got_these", "You got these")}</div>
          {rec.game === "vocab" ? (
            <div className="kid-rows">{got.map((row, i) => (
              <div key={i} className="kid-row flex items-center gap-3 py-2.5" data-testid="kid-history-got">
                <span className="w-8 h-8 rounded-full inline-flex items-center justify-center flex-shrink-0 bg-teal-50 text-teal-600" aria-hidden="true"><Icon id="ic-check" size={16} style={{ color: "currentColor" }} /></span>
                <span className="flex-1 min-w-0"><span className="block text-base font-extrabold text-slate-900">{row.q}</span><span className="block text-xs text-slate-600">{row.answer}</span></span>
              </div>
            ))}</div>
          ) : (
            <div className="flex gap-1.5 flex-wrap">{got.map((row, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-sm font-semibold rounded px-2 py-1 bg-teal-50 text-teal-800" data-testid="kid-history-got">
                <Icon id="ic-check" size={14} className="text-teal-600" style={{ color: "currentColor" }} />{rec.game === "spelling" ? row.q : (sumLine(row.q, row.answer) || `${row.q} ${row.answer}`)}
              </span>
            ))}</div>
          )}
        </div>
      )}
    </div>
  );
}

export function KidTestHistory({ history, requests = [], onClose }) {
  const [game, setGame] = useState(null);
  const [open, setOpen] = useState(null); // { game, id }
  const rec = open ? liveRecord(findTest(history, open.game, open.id), requests) : null;
  const rows = historyList(history, game).map((r) => liveRecord(r, requests));
  const back = rec ? () => setOpen(null) : onClose;
  return (
    <div className="ks-section flex flex-col gap-3.5 pt-1" data-testid="kid-history">
      <button type="button" onClick={back} className="self-start inline-flex items-center gap-1 text-sm font-bold text-indigo-800 min-h-[44px]" data-testid="kid-history-back">
        <Icon id="ic-back" size={16} style={{ color: "currentColor" }} />
        {rec ? ct("history.kid.title", "My test history") : ct("kid.learn.title", "Learn & Earn")}
      </button>
      {rec ? <KidTestDetail rec={rec} /> : (
        <>
          <h2 className="text-xl font-extrabold text-slate-900 m-0">{ct("history.kid.title", "My test history")}</h2>
          <FilterChips ids={historyGameIds(history)} value={game} onChange={setGame} kid />
          {rows.length ? (
            <div className="kid-rows">
              {rows.map((r) => (
                <button key={r.id} type="button" onClick={() => setOpen({ game: r.game, id: r.id })} className="kid-row w-full text-left flex items-center gap-3 py-2.5 min-h-[64px]" data-testid="kid-history-row">
                  <GameDisc game={r.game} kid />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-extrabold text-slate-900">{historyGameName(r.game)}</span>
                    <span className="block text-xs text-slate-500">{historyStamp(r.at)}</span>
                    {(r.stopped || r.source === "list") && (
                      <span className="flex gap-1.5 mt-1">
                        {r.stopped && <span className="text-xs font-bold rounded px-1.5 bg-amber-50 text-amber-800">{ct("history.kid.tag_stopped", "Stopped early")}</span>}
                        {r.source === "list" && <span className="text-xs font-bold rounded px-1.5 bg-teal-50 text-teal-800">{ct("history.kid.tag_list", "Grown-up's homework")}</span>}
                      </span>
                    )}
                  </span>
                  <span className="flex flex-col items-end flex-shrink-0">
                    <span className="text-base font-extrabold text-slate-900">{r.stopped ? `${r.answered}/${r.total}` : `${r.score}/${r.total}`}</span>
                    <span className="text-xs text-slate-500 whitespace-nowrap">{kidRowReward(r)}</span>
                  </span>
                  <Icon id="ic-chevron-right" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
                </button>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500 m-0" data-testid="kid-history-empty">{ct("history.kid.empty", "Finish a test and it will show up here, with every answer.")}</p>}
          {rows.length > 0 && <p className="text-xs text-slate-500 text-center m-0">{ct("history.kid.kept", "Your last {n} tests in each game are kept here.", { n: HISTORY_PER_GAME })}</p>}
        </>
      )}
    </div>
  );
}

// ---- Parent Mode ------------------------------------------------------------------------

function parentRowReward(r) {
  const s = payState(r);
  const oinks = r.oinks > 0 ? r.oinks : 0;
  if (s === "stopped") return ct("history.parent.row_none", "No reward");
  if (s === "sent") return ct("history.parent.row_waiting", "Waiting for you");
  if (s === "declined") return ct("history.parent.row_declined", "Declined");
  if (s === "paid") return oinks ? ct("history.parent.row_paid", "{tokens} tokens · {oinks} Oinks", { tokens: r.tokens, oinks }) : ct("history.parent.row_paid_tokens", "{tokens} tokens", { tokens: r.tokens });
  if (s === "used") return ct("history.parent.row_limit", "No reward · weekly limit reached");
  if (isPractice(r)) return oinks ? ct("history.parent.row_practice_oinks", "Practice · {oinks} Oinks", { oinks }) : ct("history.parent.row_practice", "Practice");
  return oinks ? ct("history.parent.row_below_oinks", "Below pass · {oinks} Oinks", { oinks }) : ct("history.parent.row_below", "Below pass");
}

function Tags({ r }) {
  if (!r.stopped && r.source !== "list") return null;
  return (
    <span className="flex gap-1.5 mt-1">
      {r.source === "list" && <span className="text-xs font-bold rounded px-1.5 bg-teal-50 text-teal-800" data-testid="history-tag-list">{ct("adaptive.hub.recent_custom", "Your list")}</span>}
      {r.stopped && <span className="text-xs font-bold rounded px-1.5 bg-amber-50 text-amber-800" data-testid="history-tag-stopped">{ct("adaptive.hub.recent_stopped", "Stopped early")}</span>}
    </span>
  );
}

// The row at the top of Coach's summary (and of Set it yourself when Coach is off): the
// way to the whole history before any game is picked (Alan, 23 Sep).
export function TestHistoryEntry({ childName, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="w-full bg-white rounded-lg px-4 py-3 flex items-center gap-3 text-left min-h-[48px]" data-testid="history-entry">
      <span className="w-8 h-8 rounded-full inline-flex items-center justify-center flex-shrink-0 bg-pill text-slate-900" aria-hidden="true"><Icon id="ic-recent" size={17} style={{ color: "currentColor" }} /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-slate-900">{ct("history.parent.entry_title", "Test history")}</span>
        <span className="block text-xs text-slate-500">{ct("history.parent.entry_sub", "Every test {name} took, with the answers", { name: childName })}</span>
      </span>
      <Icon id="ic-chevron-right" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
    </button>
  );
}

export function ParentTestHistory({ history, requests = [], initialGame = null, onOpenTest }) {
  const [game, setGame] = useState(initialGame);
  const rows = historyList(history, game).map((r) => liveRecord(r, requests));
  return (
    <div className="flex flex-col gap-3 mt-2" data-testid="history-list">
      <FilterChips ids={historyGameIds(history)} value={game} onChange={setGame} />
      {rows.length ? (
        <div className="bg-white rounded-lg overflow-hidden">
          {rows.map((r) => (
            <button key={r.id} type="button" onClick={() => onOpenTest(r.game, r.id)} className="w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 min-h-[56px]" data-testid="history-row">
              <GameDisc game={r.game} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold text-slate-900">{historyGameName(r.game)}</span>
                <span className="block text-xs text-slate-500">{historyStamp(r.at)}</span>
                <Tags r={r} />
              </span>
              <span className="flex flex-col items-end flex-shrink-0">
                <span className="text-sm font-bold text-slate-900">{scoreText(r)}</span>
                <span className="text-xs text-slate-500 whitespace-nowrap">{parentRowReward(r)}</span>
              </span>
              <Icon id="ic-chevron-right" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
            </button>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500 m-0" data-testid="history-empty">{ct("history.parent.empty", "Tests will show here from now on, with every answer.")}</p>}
      {rows.length > 0 && <p className="text-xs text-slate-500 text-center m-0">{ct("history.parent.kept", "The last {n} tests in each game are kept, newest first.", { n: HISTORY_PER_GAME })}</p>}
    </div>
  );
}

function ParentQuestionRow({ game, row, n }) {
  let line = null;
  if (!row.correct) {
    if (row.skipped) line = ct("history.parent.skipped", "Skipped");
    else if (row.timedOut) line = ct("history.parent.timed_out", "Ran out of time");
    else if (game === "spelling") line = ct("history.parent.wrote", "Wrote {given}", { given: row.given });
    else if (game === "vocab") line = ct("history.parent.picked", "Picked {given}", { given: row.given });
    else line = ct("history.parent.answered", "Answered {given}", { given: row.given });
  }
  const answerLine = game === "vocab" ? ct("history.parent.means", "Means {answer}", { answer: row.answer })
    : game === "spelling" ? null
      : (sumLine(row.q, row.answer) ? null : ct("history.parent.answer", "Answer {answer}", { answer: row.answer }));
  const title = game === "spelling" || game === "vocab" ? row.q : (row.correct ? (sumLine(row.q, row.answer) || row.q) : row.q);
  const wrongSumAnswer = !row.correct && game !== "spelling" && game !== "vocab" && sumLine(row.q, row.answer) ? ct("history.parent.answer", "Answer {answer}", { answer: row.answer }) : null;
  return (
    <div className="px-4 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-3 min-h-[44px]" data-testid="history-question">
      <span className="text-xs text-slate-500 w-5 text-right flex-shrink-0">{n}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-900 break-words">{title}</span>
        {line && <span className="block text-xs text-slate-600">{line}</span>}
        {wrongSumAnswer && <span className="block text-xs text-slate-600">{wrongSumAnswer}</span>}
        {answerLine && <span className="block text-xs text-slate-500">{answerLine}</span>}
      </span>
      {row.correct
        ? <Icon id="ic-check" size={16} className="text-teal-600 flex-shrink-0" label={ct("history.parent.right", "Right")} style={{ color: "currentColor" }} />
        : <span className="text-xs font-bold rounded px-1.5 bg-amber-50 text-amber-800 flex-shrink-0">{ct("history.parent.missed", "Missed")}</span>}
    </div>
  );
}

export function ParentTestDetail({ history, requests = [], game, id, childName }) {
  const rec = liveRecord(findTest(history, game, id), requests);
  if (!rec) return <p className="text-xs text-slate-500" data-testid="history-detail-missing">{ct("history.parent.gone", "This test is no longer in the history.")}</p>;
  const s = payState(rec);
  // Wording A (Alan, 23 Sep): the box is "reward", never "paid".
  const rewardValue = s === "paid" ? ct("history.parent.row_paid_tokens", "{tokens} tokens", { tokens: rec.tokens })
    : s === "sent" ? ct("history.parent.row_waiting", "Waiting for you")
      : s === "declined" ? ct("history.parent.row_declined", "Declined")
        : ct("history.parent.strip_none", "None");
  const passValue = rec.passMark == null ? "" : rec.passKind === "streak"
    ? ct("history.parent.pass_streak", "streak of {n}", { n: rec.passMark })
    : ct("history.parent.pass_pct", "{n}%", { n: rec.passMark });
  const why = s === "used" ? ct("history.parent.row_limit", "No reward · weekly limit reached") : s === "below" && !isPractice(rec) ? ct("history.parent.row_below", "Below pass") : null;
  return (
    <div className="flex flex-col gap-3" data-testid="history-detail">
      <p className="text-xs text-slate-500 -mt-1 m-0">{rec.source === "coach"
        ? ct("history.parent.by_coach", "{when} · chosen by Learning Coach", { when: historyStamp(rec.at) })
        : rec.source === "list" ? ct("history.parent.by_list", "{when} · from your homework", { when: historyStamp(rec.at) })
          : historyStamp(rec.at)}</p>
      <div className="bg-white rounded-lg grid grid-cols-3" data-testid="history-strip">
        <div className="px-3 py-2.5"><div className="text-base font-extrabold text-slate-900">{scoreText(rec)}</div><div className="text-xs text-slate-500">{rec.stopped ? ct("history.parent.strip_answered", "answered") : ct("history.parent.strip_score", "score")}</div></div>
        <div className="px-3 py-2.5 border-l border-slate-100"><div className="text-base font-extrabold text-slate-900">{passValue}</div><div className="text-xs text-slate-500">{ct("history.parent.strip_pass", "pass mark")}</div></div>
        <div className="px-3 py-2.5 border-l border-slate-100"><div className="text-base font-extrabold text-slate-900" data-testid="history-reward">{rewardValue}</div><div className="text-xs text-slate-500">{rec.oinks > 0 ? ct("history.parent.strip_reward_oinks", "reward · {n} Oinks", { n: rec.oinks }) : ct("history.parent.strip_reward", "reward")}</div></div>
      </div>
      {rec.stopped && <p className="text-xs text-slate-500 m-0" data-testid="history-stopped-note">{ct("history.parent.stopped_note", "{name} stopped after {answered} of {total} questions. A stopped test never pays and doesn't move the level.", { name: childName, answered: rec.answered, total: rec.total })}</p>}
      {why && <p className="text-xs text-slate-500 m-0">{why}</p>}
      {rec.game === "ttstreaks" && rec.streak != null && <p className="text-xs text-slate-500 m-0">{ct("history.parent.streak", "Best streak {n}", { n: rec.streak })}</p>}
      <div className="text-sm font-bold text-slate-900 mt-1">{ct("history.parent.in_order", "In the order asked")}</div>
      <div className="bg-white rounded-lg overflow-hidden">{rec.questions.map((row, i) => <ParentQuestionRow key={i} game={rec.game} row={row} n={i + 1} />)}</div>
      {rec.source === "list" && <p className="text-xs text-slate-500 m-0">{ct("adaptive.hub.recent_custom_note", "Tests from your own list count, and the tricky words come back. They don't set the level, because those words are practised before the test.")}</p>}
    </div>
  );
}

// "Tuesday" inside the last week, "9 Sep" beyond it — the legacy rows below carry a day
// only (the Coach log never had a time), so they keep the label they always had.
function legacyDayLabel(day, today) {
  if (!day) return "";
  const d = new Date(day + "T00:00:00");
  if (Number.isNaN(d.getTime())) return day;
  const days = Math.round((Date.parse(today) - Date.parse(day)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return d.toLocaleDateString("en-GB", { weekday: "long" });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// The per-game Recent tests card. Once a game has any history records they are what it
// shows (time, tappable); until then it shows the Coach log as it always did, because that
// is the only record of tests taken before this release.
export function RecentTestsCard({ learning, game, today, onOpenTest, onSeeAll, variant = "coach" }) {
  const history = (learning && learning.testHistory) || {};
  const recs = historyList(history, game).slice(0, 3);
  const log = (((learning || {}).adaptive || {})[game] || {}).log || [];
  const legacy = recs.length ? [] : log.slice(-3).reverse();
  const coach = variant === "coach";
  const pad = coach ? "px-4" : "px-3";
  return (
    <div className={coach ? "bg-white rounded-lg overflow-hidden" : "border-t border-slate-100"} data-testid={coach ? "adaptive-recent" : `manual-recent-${game}`}>
      <div className={`text-xs font-bold ${coach ? "text-slate-900 px-4 pt-3 pb-1.5" : "text-slate-800 px-3 pt-2 pb-1"}`}>{coach ? ct("adaptive.hub.recent_tests", "Recent tests") : ct("learnearn.recent_title", "Recent tests")}</div>
      {recs.map((r) => (
        <button key={r.id} type="button" onClick={() => onOpenTest && onOpenTest(game, r.id)} disabled={!onOpenTest} className={`w-full text-left ${pad} py-2.5 border-t border-slate-100 flex items-center justify-between gap-2 text-xs min-h-[44px]`} data-testid={coach ? "adaptive-recent-row" : "manual-recent-row"}>
          <span className="text-slate-500">{historyStamp(r.at)}</span>
          <span className="flex items-center gap-2">
            {r.source === "list" && <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-teal-50 text-teal-800" data-testid="adaptive-recent-custom">{ct("adaptive.hub.recent_custom", "Your list")}</span>}
            {r.stopped && <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-amber-50 text-amber-800" data-testid="adaptive-recent-stopped">{ct("adaptive.hub.recent_stopped", "Stopped early")}</span>}
            <span className="font-bold text-slate-800">{scoreText(r)}</span>
            {onOpenTest && <Icon id="ic-chevron-right" size={14} className="text-slate-400" style={{ color: "currentColor" }} />}
          </span>
        </button>
      ))}
      {legacy.map((e, i) => (
        <div key={`l${i}`} className={`${pad} py-2.5 border-t border-slate-100 flex items-center justify-between text-xs`} data-testid={coach ? "adaptive-recent-row" : "manual-recent-row"}>
          <span className="text-slate-500">{legacyDayLabel(e.day, today)}</span>
          <span className="flex items-center gap-2">
            {e.source === "manual" && coach && <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-teal-50 text-teal-800" data-testid="adaptive-recent-custom">{ct("adaptive.hub.recent_custom", "Your list")}</span>}
            {e.source === "stopped" && <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-amber-50 text-amber-800" data-testid="adaptive-recent-stopped">{ct("adaptive.hub.recent_stopped", "Stopped early")}</span>}
            <span className="font-bold text-slate-800">{e.source === "stopped"
              ? ct("adaptive.hub.recent_stopped_score", "{answered} of {total}", { answered: e.answered ?? e.score, total: e.total })
              : ct("adaptive.hub.recent_score", "{score} of {total}", { score: e.score, total: e.total })}</span>
          </span>
        </div>
      ))}
      {!recs.length && !legacy.length && (coach
        ? <p className="text-xs text-slate-500 px-4 pb-3">{ct("adaptive.hub.recent_none", "No tests yet")}</p>
        : <p className="text-xs text-slate-400 px-3 pb-2.5 pt-0.5 m-0">{ct("learnearn.recent_empty", "Tests will show here from now on.")}</p>)}
      {coach && (recs.some((r) => r.source === "list") || legacy.some((e) => e.source === "manual")) && (
        <p className="text-xs text-slate-400 px-4 pt-2 pb-3 leading-relaxed">{ct("adaptive.hub.recent_custom_note", "Tests from your own list count, and the tricky words come back. They don't set the level, because those words are practised before the test.")}</p>
      )}
      {onSeeAll && recs.length > 0 && (
        <button type="button" onClick={() => onSeeAll(game)} className={`w-full text-left ${pad} border-t border-slate-100 flex items-center justify-between text-sm font-bold text-slate-900 min-h-[48px]`} data-testid="history-see-all">
          <span>{ct("history.parent.see_all", "See all {game} tests", { game: historyGameName(game) })}</span>
          <Icon id="ic-chevron-right" size={16} className="text-slate-400" style={{ color: "currentColor" }} />
        </button>
      )}
    </div>
  );
}
